import { useCallback, useMemo } from 'react';
import { Icon } from '@cbhq/cds-web/icons';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { ActionItem } from '../ProfileMenu/components/ActionItem';
import { FallbackIcon } from '../ProfileMenu/components/FallbackIcon';
import { useAnalytics } from '../ProfileMenu/hooks/useAnalytics';
import { useNavigate } from '../ProfileMenu/hooks/useNavigate';
import { MenuActionItem } from '../ProfileMenu/types';

type HelpMenuActionItemProps = {
  apiAction: MenuActionItem;
};

export function HelpMenuActionItem({ apiAction }: HelpMenuActionItemProps): JSX.Element {
  const { logEvent } = useAnalytics();

  const navigate = useNavigate();
  const menuItemPressHandler = useCallback(() => {
    logEvent('help_menu_action', {
      action: ActionType.click,
      componentType: ComponentType.dropdown,
      actionKey: apiAction.key,
    });
    void navigate(apiAction.callbackUrl, apiAction.key);
  }, [apiAction.callbackUrl, apiAction.key, logEvent, navigate]);

  const icon = useMemo(() => {
    if (!apiAction.icon) {
      return <FallbackIcon />;
    }
    return (
      <Icon
        name={apiAction.icon}
        size="m"
        color="fg"
        fallback={
          // Not all clients have the CDS version that merges Icon and NavigationIcon.
          // This fallback helps us avoid fallback on those scenarios.
          <Icon name={apiAction.icon} size="m" fallback={<FallbackIcon />} color="fg" />
        }
      />
    );
  }, [apiAction.icon]);

  return (
    <ActionItem
      label={apiAction.label}
      description={apiAction.description}
      icon={icon}
      imageUrl={apiAction.imageUrl}
      negative={apiAction.key === 'sign_out'}
      onPress={menuItemPressHandler}
      testID={`help-menu-action-item-${apiAction.key}`}
    />
  );
}
